// src/encounter/nurse-encounter-signal/nurse-encounter-rules.ts
// `signalNurseEncounter` (Samer, 4 Nov, VPS demo): the NURSE's encounter after Start, to Antonin's Claude Design page 56
// (56-zones-encounter-nurse.html) and the encounter rule sheet's rulings of 4 Nov. Nurse only; the physician and allied health
// never reach this file's rules (every helper here asks for the author first).
//
//   ROS   Absent / Present only (page 56 and 17-encounter-ros.html: the nurse's lines carry no "Not asked" and no "Unsure"),
//         and no Stridor / Apnea / Choking line for the nurse. "Absent" writes what "Denies" writes; "Present" what "Reports"
//         writes (the same finding linkIds, the same booleans). A second click on the chosen chip clears the line, as on the exam.
//   PE    no physical exam for the nurse: her Objective keeps the vitals only. Nothing is written for the exam.
//   Pain  Present + NRS 0-10 + the location only (structured-note/risk-record-lines.ts `isNursePainDetail`); a detail that already
//         holds an answer still shows. Fall stays the CDC STEADI follow-up it already is.
//
// The switch only removes choices and lines from the nurse's screen. It adds no field and no write path: every answer still goes
// through the structured note's own write (the encounter's answer store), unchanged.
import type { AuthorRole } from '@scripsteam/scrips-signal-ds';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { SystemLine, SystemState } from '../structured-note/system-lines-model';

/** True for the nurse with `signalNurseEncounter` on (dev hosts by default). Every other author: false. */
export function isSignalNurseEncounter(author: AuthorRole | string | null | undefined): boolean {
  return author === 'nurse' && isEncounterFlagOn('signalNurseEncounter');
}

/** The nurse's ROS line choices (page 56): Absent, then Present. "Present" only when the system has a finding to pick. */
export const NURSE_ROS_OPTIONS: readonly { value: SystemState; label: string }[] = [
  { value: 'negative', label: 'Absent' },
  { value: 'positive', label: 'Present' },
];

export function nurseRosOptions(line: SystemLine): readonly { value: SystemState; label: string }[] {
  const hasPick = line.findings.some((f) => !f.risk && f.kind === 'finding');
  return NURSE_ROS_OPTIONS.filter((o) => hasPick || o.value !== 'positive');
}

/** A risk finding's own line for the nurse: Present / Absent, no "Not asked" (a second click clears it). */
export const NURSE_ROS_RISK_OPTIONS: readonly { value: 'yes' | 'no'; label: string }[] = [
  { value: 'no', label: 'Absent' },
  { value: 'yes', label: 'Present' },
];

/** The ROS findings the nurse is not asked (4 Nov rulings): matched on the template's own words. */
const NURSE_ROS_EXCLUDED = /\b(stridor|apno?eas?|choking)\b/i;

export const isNurseExcludedFinding = (text: string): boolean => NURSE_ROS_EXCLUDED.test(text);

/** The nurse's ROS lines: the excluded findings removed (neither drawn nor written by a system-wide choice), and a system left
 *  with no finding dropped. */
export function nurseRosLines(lines: readonly SystemLine[]): SystemLine[] {
  return lines
    .map((line) => ({ ...line, findings: line.findings.filter((f) => !isNurseExcludedFinding(f.text)) }))
    .filter((line) => line.findings.length > 0);
}
